<template>
  <div class="cwg">
    <!-- ค้นหาเมนู + ทางลัดไปแต่ละกลุ่มงาน -->
    <v-card flat class="cwg-toolbar mb-3">
      <v-card-text class="pa-3">
        <v-text-field
          v-model.trim="search"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          label="ค้นหาเมนู เช่น ย้าย, ครูผู้ช่วย, รายงานตัว, ตำแหน่งว่าง"
          class="mb-2"
        />
        <div v-if="!search" class="cwg-jump">
          <v-chip
            v-for="g in groups"
            :key="g.key"
            small
            outlined
            color="#263238"
            class="mr-1 mb-1"
            @click="scrollTo(g.anchor)"
          >
            <v-icon left small>{{ g.icon }}</v-icon>{{ g.title }}
            <v-avatar v-if="g.pending" right color="red darken-1" class="white--text cwg-badge">{{ g.pending }}</v-avatar>
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <!-- ผลการค้นหา -->
    <v-card v-if="search" class="cwg-card mb-3">
      <div class="cwg-card-header"><v-icon size="18" class="mr-2">mdi-magnify</v-icon>ผลการค้นหา “{{ search }}”</div>
      <div v-if="!searchResults.length" class="pa-4 grey--text">ไม่พบเมนูที่ตรงกับคำค้น</div>
      <v-list v-else dense>
        <v-list-item v-for="m in searchResults" :key="m.to" :to="m.to">
          <v-list-item-icon class="mr-3"><v-icon>{{ m.icon }}</v-icon></v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="cwg-menu-text">{{ m.text }}</v-list-item-title>
            <v-list-item-subtitle>{{ m.groupTitle }}<span v-if="m.hint"> · {{ m.hint }}</span></v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="m.pending">
            <v-chip x-small color="red darken-1" dark>ค้าง {{ m.pending }}</v-chip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- การ์ดกลุ่มงาน -->
    <v-row v-else dense>
      <v-col v-for="g in groups" :key="g.key" cols="12" md="6">
        <v-card :id="g.anchor" class="cwg-card" :class="{ 'cwg-card--alert': g.pending }">
          <div class="cwg-card-header">
            <v-icon size="18" class="mr-2">{{ g.icon }}</v-icon>
            <span class="cwg-title">{{ g.title }}</span>
            <v-chip v-if="g.pending" x-small color="red darken-1" dark class="ml-2">
              ค้าง {{ g.pending }} ภารกิจ
            </v-chip>
            <v-chip v-else-if="g.hasTasks" x-small color="success" dark class="ml-2">
              <v-icon left x-small>mdi-check</v-icon>ไม่มีงานค้าง
            </v-chip>
            <v-spacer />
            <v-btn v-if="g.video" icon small title="วิดีโอแนะนำ" @click="$emit('video', g.video)">
              <v-icon small>mdi-youtube</v-icon>
            </v-btn>
          </div>

          <div class="cwg-desc">{{ g.desc }}</div>

          <v-list dense class="py-0">
            <v-list-item v-for="m in g.menus" :key="m.to" :to="m.to" class="cwg-menu">
              <v-list-item-icon class="mr-3"><v-icon>{{ m.icon }}</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="cwg-menu-text">{{ m.text }}</v-list-item-title>
                <v-list-item-subtitle v-if="m.hint">{{ m.hint }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="m.pending" class="my-0">
                <v-chip x-small color="red darken-1" dark>ค้าง {{ m.pending }}</v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <div v-if="g.flow.length" class="cwg-footer">
            <v-btn small text color="primary" @click="openGuide(g.key)">
              <v-icon left small>mdi-format-list-numbered</v-icon>ขั้นตอนการทำงานของกลุ่มงานนี้
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <CollegeTaskGuide v-model="guideOpen" :group-key="guideGroup" :tasks="tasks" />
  </div>
</template>

<script>
// เมนูสถานศึกษาแบ่งตามกลุ่มงาน — เนื้อหาเมนู/คู่มืออยู่ที่ collegeTaskCatalog.js
// tasks (จาก CollegeNotificationCenter) ใช้แสดงจำนวนภารกิจค้างของแต่ละกลุ่ม/เมนู
import { GROUPS, GROUP_ORDER } from "./collegeTaskCatalog";
import CollegeTaskGuide from "./CollegeTaskGuide.vue";

export default {
  name: "CollegeWorkGroups",

  components: { CollegeTaskGuide },

  props: {
    tasks: { type: Array, default: () => [] }
  },

  data() {
    return { search: "", guideOpen: false, guideGroup: null };
  },

  computed: {
    pending() {
      return this.tasks.filter(
        t => (t.status === "todo" || t.status === "partial") && t.period_state !== "upcoming"
      );
    },
    groups() {
      return GROUP_ORDER.map(key => {
        const g = GROUPS[key];
        const pending = this.pending.filter(t => t.group === key);
        return {
          ...g,
          key,
          pending: pending.length,
          hasTasks: this.tasks.some(t => t.group === key),
          menus: g.menus.map(m => ({ ...m, pending: pending.filter(t => t.route === m.to).length }))
        };
      });
    },
    searchResults() {
      const words = this.search.toLowerCase().split(/\s+/).filter(Boolean);
      const out = [];
      this.groups.forEach(g => {
        g.menus.forEach(m => {
          const hay = `${m.text} ${m.hint || ""} ${g.title} ${g.desc}`.toLowerCase();
          if (words.every(w => hay.includes(w))) out.push({ ...m, groupTitle: g.title });
        });
      });
      return out;
    }
  },

  methods: {
    openGuide(key) {
      this.guideGroup = key;
      this.guideOpen = true;
    },
    // router ใช้ hash mode — ลิงก์ href="#anchor" จะถูกตีความเป็น route จึงเลื่อนด้วย JS แทน
    scrollTo(anchor) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
};
</script>

<style scoped>
.cwg-toolbar {
  border: 1px solid #e4e4e7 !important;
  border-radius: 12px !important;
}
.cwg-badge {
  font-size: 0.7rem !important;
}
.cwg-card {
  border-radius: 14px !important;
  border: 1px solid #e4e4e7 !important;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  scroll-margin-top: 80px;
}
.cwg-card--alert {
  border-color: #ef9a9a !important;
}
.cwg-card-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: #f8fafc;
  color: #1b365d;
  border-bottom: 1px solid #e2e8f0;
  border-left: 3px solid #b3151b;
  font-size: 0.92rem;
  font-weight: 700;
  padding: 10px 14px;
  min-height: 46px;
}
.cwg-card-header > .v-icon {
  color: #b3151b !important;
}
.cwg-desc {
  font-size: 0.8rem;
  color: #607d8b;
  padding: 8px 16px 2px;
}
.cwg-menu-text {
  white-space: normal;
  line-height: 1.35;
}
.cwg-footer {
  margin-top: auto;
  border-top: 1px dashed #e2e8f0;
  padding: 4px 8px;
}
</style>
